/**
 * QualityScorecard — shared display for PIS / RRQS / RHC results.
 *
 * Props:
 *   data:       { scorecard, top_gaps, remediation, source }
 *   subjectLabel: 'risk register' | 'plan portfolio'
 */

const LEVEL_LABEL = { 1: "Ad hoc", 2: "Repeatable", 3: "Defined", 4: "Managed", 5: "Optimising" };
const LEVEL_COLOR = {
  1: "bg-rose-500",
  2: "bg-orange-400",
  3: "bg-amber-400",
  4: "bg-emerald-500",
  5: "bg-emerald-600",
};

function levelFromScore(s) {
  if (s >= 0.85) return 5;
  if (s >= 0.7) return 4;
  if (s >= 0.5) return 3;
  if (s >= 0.3) return 2;
  return 1;
}

export function QualityScorecard({ data, subjectLabel, testidPrefix = "qs" }) {
  if (!data) return null;
  const s = data.scorecard;
  const provenance = data.remediation?.provenance || "rule-derived";

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-scorecard`}>
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-violet-600 font-semibold">{s.engine === "rrqs" ? "Risk Register Quality" : "Plan Integrity"} · v{s.engine_version}</p>
            <div className="mt-2 flex items-baseline gap-3">
              <h2 className="text-4xl font-bold tracking-tight text-violet-900" data-testid={`${testidPrefix}-level`}>Level {s.overall_level}</h2>
              <span className="text-xl font-semibold text-slate-700">{s.overall_level_label}</span>
            </div>
            <p className="mt-1 text-sm text-slate-500">Composite <strong>{s.overall_score.toFixed(3)}</strong> · Confidence <strong>{s.confidence}</strong> · {s.sample_size} {subjectLabel} item(s) scored · Source: <strong>{data.source}</strong></p>
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white ${LEVEL_COLOR[s.overall_level]}`}>
            {LEVEL_LABEL[s.overall_level]}
          </span>
        </div>
        <p className="mt-4 rounded-lg bg-violet-50/60 px-4 py-2 text-xs text-violet-700"><strong>Defensible AI Charter:</strong> remediation hints are <strong>{provenance}</strong> — anchored to the specific rule that triggered them, with no model invention.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {s.checks.map((c) => {
          const lvl = levelFromScore(c.score);
          return (
            <div key={c.check_id} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-100" data-testid={`${testidPrefix}-check-${c.check_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-slate-800">{c.name}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{c.pmo360_dimension} · weight {c.weight}</p>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white ${LEVEL_COLOR[lvl]}`}>
                  {(c.score * 100).toFixed(0)}%
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">{c.verdict}</p>
              {c.failed > 0 && c.failed_sample?.length > 0 && (
                <details className="mt-2 text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-violet-700">Show {Math.min(c.failed_sample.length, 10)} sample IDs</summary>
                  <p className="mt-1 font-mono text-[11px] break-all">{c.failed_sample.join(", ")}</p>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {data.top_gaps?.length > 0 && (
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200" data-testid={`${testidPrefix}-gaps`}>
          <h3 className="text-lg font-semibold text-violet-900">Top {data.top_gaps.length} remediation focus areas</h3>
          <p className="text-xs text-slate-500 mb-4">Lowest-scoring checks weighted by importance. Hints below are <strong>{provenance}</strong>.</p>
          <ul className="space-y-4">
            {data.top_gaps.map((g) => (
              <li key={g.check_id} className="rounded-lg border-l-4 border-violet-400 bg-violet-50/40 px-4 py-3" data-testid={`${testidPrefix}-gap-${g.check_id}`}>
                <p className="font-medium text-slate-800">{g.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{g.verdict}</p>
                <ul className="mt-2 list-disc pl-5 text-sm text-slate-700 space-y-1">
                  {(data.remediation?.hints?.[g.check_id] || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
